import { Preferences } from '@capacitor/preferences'
import { ActivityEntry } from './api.types'

// Interface for queued activities with retry metadata
export interface QueuedActivity {
  id: string
  activity: ActivityEntry
  routeData?: any[] // Optional GPS route data
  timestamp: number
  retryCount: number
  nextRetryAt: number
  lastError?: string
}

// Configuration for retry logic
export interface RetryConfig {
  maxRetries: number
  baseDelayMs: number
  maxDelayMs: number
  backoffMultiplier: number
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 5,
  baseDelayMs: 1000, // 1 second
  maxDelayMs: 300000, // 5 minutes
  backoffMultiplier: 2,
}

const QUEUE_STORAGE_KEY = 'offline_activity_queue'

/**
 * Service for managing offline activity uploads with retry logic
 * Provides persistent storage, exponential backoff, and automatic retry
 */
export class OfflineActivityQueue {
  private retryConfig: RetryConfig
  private queue: QueuedActivity[] = []
  private isProcessing = false

  constructor(config: Partial<RetryConfig> = {}) {
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config }
    this.loadQueue()
  }

  /**
   * Add an activity to the offline queue
   * @param activity - The activity data to queue
   * @param routeData - Optional GPS route data
   * @returns The generated queue item ID
   */
  async addToQueue(activity: ActivityEntry, routeData?: any[]): Promise<string> {
    const id = this.generateId()
    const queuedActivity: QueuedActivity = {
      id,
      activity,
      routeData,
      timestamp: Date.now(),
      retryCount: 0,
      nextRetryAt: Date.now(), // Ready for immediate processing
    }

    this.queue.push(queuedActivity)
    await this.saveQueue()

    console.log('📦 OfflineActivityQueue: Added activity to queue', { id, activityType: activity.activityType })
    return id
  }

  /**
   * Get all queued activities
   * @returns Array of queued activities
   */
  getQueue(): QueuedActivity[] {
    return [...this.queue]
  }

  /**
   * Get count of pending activities
   * @returns Number of activities in queue
   */
  getQueueCount(): number {
    return this.queue.length
  }

  /**
   * Remove an activity from the queue (after successful upload)
   * @param id - The queue item ID to remove
   */
  async removeFromQueue(id: string): Promise<void> {
    const initialLength = this.queue.length
    this.queue = this.queue.filter((item) => item.id !== id)

    if (this.queue.length !== initialLength) {
      await this.saveQueue()
      console.log('✅ OfflineActivityQueue: Removed activity from queue', { id })
    }
  }

  /**
   * Mark an activity as failed and schedule for retry
   * @param id - The queue item ID
   * @param error - The error message
   */
  async markAsFailed(id: string, error: string): Promise<void> {
    const item = this.queue.find((q) => q.id === id)
    if (!item) return

    item.retryCount++
    item.lastError = error

    if (item.retryCount >= this.retryConfig.maxRetries) {
      console.error('❌ OfflineActivityQueue: Max retries exceeded, removing from queue', {
        id,
        retryCount: item.retryCount,
        error,
      })
      await this.removeFromQueue(id)
      return
    }

    // Calculate next retry time using exponential backoff
    const delay = Math.min(
      this.retryConfig.baseDelayMs * Math.pow(this.retryConfig.backoffMultiplier, item.retryCount - 1),
      this.retryConfig.maxDelayMs,
    )

    item.nextRetryAt = Date.now() + delay
    await this.saveQueue()

    console.warn('⚠️ OfflineActivityQueue: Scheduled retry', {
      id,
      retryCount: item.retryCount,
      nextRetryAt: new Date(item.nextRetryAt).toISOString(),
      error,
    })
  }

  /**
   * Get activities that are ready for retry
   * @returns Array of activities ready for processing
   */
  getReadyForRetry(): QueuedActivity[] {
    const now = Date.now()
    return this.queue.filter((item) => item.nextRetryAt <= now)
  }

  /**
   * Clear all activities from the queue
   */
  async clearQueue(): Promise<void> {
    this.queue = []
    await this.saveQueue()
    console.log('🗑️ OfflineActivityQueue: Cleared all queued activities')
  }

  /**
   * Load queue from persistent storage
   */
  private async loadQueue(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: QUEUE_STORAGE_KEY })
      if (value) {
        const parsed = JSON.parse(value)
        this.queue = Array.isArray(parsed) ? parsed : []
        console.log('📥 OfflineActivityQueue: Loaded queue from storage', { count: this.queue.length })
      }
    } catch (error) {
      console.error('❌ OfflineActivityQueue: Failed to load queue from storage', error)
      this.queue = []
    }
  }

  /**
   * Save queue to persistent storage
   */
  private async saveQueue(): Promise<void> {
    try {
      await Preferences.set({
        key: QUEUE_STORAGE_KEY,
        value: JSON.stringify(this.queue),
      })
    } catch (error) {
      console.error('❌ OfflineActivityQueue: Failed to save queue to storage', error)
    }
  }

  /**
   * Generate a unique ID for queue items
   */
  private generateId(): string {
    return `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}

// Export singleton instance
export const offlineActivityQueue = new OfflineActivityQueue()
